/**
 * TalentIQ design tokens — the single source of truth for colors, radius,
 * shadows, and typography scale. Import these instead of hardcoding hex/px
 * values inside components so the whole app stays visually consistent.
 *
 * Migration note: components currently hold their own color constants
 * (e.g. Test.js `C = {...}`, Assesment.js palettes). Point those at these
 * tokens incrementally — no behavior changes, just shared values.
 */

// ---- Color palette --------------------------------------------------------
export const colors = {
  // Brand blue — the dominant in-app primary (#1c97e3 was used ~275×).
  primary: {
    main: "#1c97e3",
    light: "#62a7f8",
    dark: "#0a73b1",
    darker: "#1565c0",
    contrastText: "#ffffff",
  },
  // Semantic
  success: { main: "#16a34a", soft: "#dcfce7", dark: "#15803d" },
  warning: { main: "#f59e0b", soft: "#fef3c7", dark: "#b45309" },
  error: { main: "#d32f2f", soft: "#ffebee", dark: "#b91c1c" },
  info: { main: "#0ea5e9", soft: "#e0f2fe", dark: "#075985" },

  // Neutrals / text
  ink: "#0f172a", // headings
  body: "#1e293b", // primary body text
  muted: "#7b8794", // secondary / captions
  line: "#e3e9f0", // borders / dividers
  bg: "#f4f7fb", // app background
  surface: "#ffffff", // cards / sheets
};

// Shuffled accent palette for "self assessment" style cards (kept here so
// it can be reused beyond the Assessment screen).
export const accentPalette = [
  { accent: "#7c3aed", soft: "#f5f0ff", border: "#e4d4fb", ink: "#5b21b6" }, // purple
  { accent: "#0ea5e9", soft: "#eef8ff", border: "#c7e8fb", ink: "#075985" }, // sky
  { accent: "#16a34a", soft: "#eefbf1", border: "#c2eecd", ink: "#166534" }, // green
  { accent: "#f59e0b", soft: "#fff8eb", border: "#fae4b3", ink: "#92400e" }, // amber
  { accent: "#f43f5e", soft: "#fff0f2", border: "#fbd2d8", ink: "#9f1239" }, // rose
  { accent: "#0d9488", soft: "#edfbf8", border: "#bdeee5", ink: "#115e59" }, // teal
];

// ---- Radius ---------------------------------------------------------------
export const radius = {
  sm: 8,
  input: 10,
  button: 12,
  card: 16,
  pill: 999,
};

// ---- Shadows (elevation scale) -------------------------------------------
export const shadow = {
  sm: "0 2px 6px rgba(20,40,80,0.05)",
  base: "0 4px 14px rgba(20,40,80,0.06)",
  md: "0 8px 24px rgba(20,40,80,0.10)",
  lg: "0 16px 32px rgba(20,40,80,0.14)",
  modal: "0 24px 60px rgba(0,0,0,0.18)",
};

// ---- Typography scale -----------------------------------------------------
export const fontSize = {
  pageTitle: { xs: 22, sm: 28 }, // h1 — responsive
  sectionTitle: 20,
  cardTitle: 18,
  subtitle: 15,
  body: 14,
  small: 13,
  caption: 12,
  overline: 10.5,
};

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  heavy: 800,
};

const tokens = { colors, accentPalette, radius, shadow, fontSize, fontWeight };
export default tokens;
